import {
  ESCALATION_STATUSES,
  ESCALATION_STATUS_LABELS,
  MANAGE_ESCALATIONS_PERMISSION,
  type EscalationRow,
  type EscalationStatus,
  type FieldDefinition,
  type Filter,
} from "@estelle/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Download, ListChecks, Loader2, Mail } from "lucide-react";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { renderCell } from "@/components/table/cells";
import { csvFilename, downloadCsv } from "@/components/table/csv";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { SelectionBar } from "@/components/table/SelectionBar";
import {
  SELECT_ALL_CAP,
  selectionFacts,
  useSelection,
} from "@/components/table/selection";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { reportingNow } from "@/lib/reportClock";
import {
  awbLines,
  daysLabel,
  escalationCsv,
  orderNumberLines,
} from "@/modules/orders/escalationActions";
import { projectEscalationRow } from "@/modules/orders/projections";
import { EscalationDetail } from "@/modules/orders/screens/EscalationDetail";
import {
  EmailCourierDialog,
  EscalationMailBar,
} from "@/modules/orders/screens/EscalationEmail";
import {
  KindBadge,
  SentBefore,
  StatusBadge,
} from "@/modules/orders/screens/EscalationParts";
import { exactIndiaTime, timeAgo } from "@/modules/orders/tracking";
import type { SectionScreenProps } from "@/modules/types";
import { useOpenRecord } from "@/routes/useOpenRecord";

/**
 * Orders > Escalations (#702): every parcel that failed delivery, went quiet or
 * was lost, one row per shipment and kind, so an order with two shipments can
 * appear twice, each with its own AWB.
 *
 * The four tabs are standing filters ANDed with the person's own, never
 * substituted for it: narrowing by courier survives a change of tab. Everything
 * else (the toolbar, the filters, the saved views, the columns, the sort, the
 * URL) is the shared framework's.
 *
 * Nothing here shows or copies a customer's name, phone or address. The bulk
 * actions work on the rows ticked, kept across pages and tabs' filters.
 */

const NOUN = "escalations";

/** A filter with the tab's status ANDed in front. */
function withStatus(status: EscalationStatus, filter: Filter): Filter {
  return {
    kind: "group",
    combinator: "and",
    children: [
      { kind: "condition", field: "status", operator: "is", value: status },
      filter,
    ],
  };
}

function Tabs({
  active,
  counts,
  onChange,
}: {
  active: EscalationStatus;
  counts: Record<EscalationStatus, number> | undefined;
  onChange: (status: EscalationStatus) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Escalation status"
      className="flex shrink-0 gap-1 overflow-x-auto border-b px-3 pt-2"
    >
      {ESCALATION_STATUSES.map((status) => {
        const selected = status === active;
        return (
          <button
            key={status}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => {
              onChange(status);
            }}
            className={cn(
              "inline-flex min-h-11 items-center gap-2 border-b-2 px-3 text-sm font-medium whitespace-nowrap",
              selected
                ? "border-primary text-foreground"
                : "text-muted-foreground hover:text-foreground border-transparent",
            )}
          >
            {ESCALATION_STATUS_LABELS[status]}
            <span
              className={cn(
                "rounded-full px-1.5 text-xs tabular-nums",
                selected
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {counts ? counts[status] : "–"}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function EscalationMobileCard({ row }: { row: EscalationRow }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-base font-semibold">
          {row.orderNumber}
        </span>
        <span className="text-muted-foreground min-w-0 truncate text-sm">
          {row.courier ?? "Courier not set"}
        </span>
      </div>
      <p className="text-muted-foreground text-sm tabular-nums wrap-anywhere">
        {row.awb ?? "No AWB"}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <KindBadge kind={row.kind} level={row.level} />
        {row.payment ? (
          <span className="text-muted-foreground text-xs">
            {row.payment === "cod" ? "COD" : "Prepaid"}
          </span>
        ) : null}
      </div>
      <SentBefore row={row} />
      <p className="text-muted-foreground text-sm">
        Open {daysLabel(row.daysOpen).toLowerCase()}
        {row.attempts > 0 ? ` · ${String(row.attempts)} attempts` : ""}
      </p>
    </div>
  );
}

export function EscalationsList({
  fields,
  filter,
  search,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const canManage = can(permissions, MANAGE_ESCALATIONS_PERMISSION);
  const [tab, setTab] = useState<EscalationStatus>("new");
  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  const { recordId, open } = useOpenRecord();
  const selection = useSelection<EscalationRow>((row) => row.id);
  const [note, setNote] = useState<string | null>(null);
  const [emailIds, setEmailIds] = useState<string[] | null>(null);

  const effective = withStatus(tab, filter);

  const counts = useQuery({
    queryKey: ["escalations", "counts"],
    queryFn: () => data.escalations.counts(),
    refetchInterval: 60_000,
  });

  const query = useQuery({
    queryKey: ["escalations", "list", effective, search, sort, limit, cursor],
    queryFn: () =>
      data.escalations.list({ filter: effective, search, sort, limit, cursor }),
  });

  const markSent = useMutation({
    mutationFn: (ids: string[]) => data.escalations.markSent(ids),
    onSuccess: (result) => {
      if (result.ok) {
        setNote(
          `Marked ${String(result.data.updated)} as sent. They are on the Sent tab.`,
        );
        selection.clear();
      } else {
        setNote(result.rejection.message);
      }
      void queryClient.invalidateQueries({ queryKey: ["escalations"] });
    },
  });

  function renderOverride(field: FieldDefinition, row: EscalationRow) {
    switch (field.name) {
      case "kind":
        return (
          <span className="inline-block">
            <KindBadge kind={row.kind} level={row.level} />
            <SentBefore row={row} />
          </span>
        );
      case "status":
        return <StatusBadge status={row.status} />;
      case "courier":
        return row.courier ?? renderCell(field, null);
      case "lastScanAt":
        return row.lastScanAt ? (
          <span title={exactIndiaTime(row.lastScanAt)}>
            {timeAgo(row.lastScanAt, reportingNow())}
          </span>
        ) : (
          renderCell(field, null)
        );
      case "daysOpen":
        return daysLabel(row.daysOpen);
      case "payment":
        return row.payment === null
          ? renderCell(field, null)
          : row.payment === "cod"
            ? "COD"
            : "Prepaid";
      default:
        return undefined;
    }
  }

  if (query.isPending) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <Tabs active={tab} counts={counts.data} onChange={setTab} />
        <TableSkeleton />
      </div>
    );
  }
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const rows = query.data.items;
  const picked = [...selection.rows.values()].slice(0, SELECT_ALL_CAP);
  const facts = selectionFacts({
    selection,
    pageRows: rows,
    idOf: (row) => row.id,
  });
  const newPicked = picked.filter((row) => row.status === "new");

  async function selectAllMatching() {
    const found: EscalationRow[] = [];
    let next: string | undefined;
    do {
      const page = await data.escalations.list({
        filter: effective,
        search,
        sort,
        limit: 100,
        cursor: next,
      });
      found.push(...page.items);
      next = page.nextCursor ?? undefined;
    } while (next && found.length < SELECT_ALL_CAP);
    selection.setMany(found.slice(0, SELECT_ALL_CAP), true);
  }

  function copy(text: string, what: string, count: number) {
    void navigator.clipboard.writeText(text).then(() => {
      setNote(`Copied ${String(count)} ${what}.`);
    });
  }

  const actions = (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="min-h-11 sm:min-h-8"
        onClick={() => {
          const text = awbLines(picked);
          copy(text, "AWBs", text === "" ? 0 : text.split("\n").length);
        }}
      >
        <Copy aria-hidden />
        Copy AWBs
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="min-h-11 sm:min-h-8"
        onClick={() => {
          const text = orderNumberLines(picked);
          copy(
            text,
            "order numbers",
            text === "" ? 0 : text.split("\n").length,
          );
        }}
      >
        <ListChecks aria-hidden />
        Copy order numbers
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="min-h-11 sm:min-h-8"
        onClick={() => {
          downloadCsv(
            csvFilename(
              "escalations",
              tab,
              new Date(reportingNow()).toISOString(),
            ),
            escalationCsv(picked),
          );
          setNote(`Exported ${String(picked.length)} rows.`);
        }}
      >
        <Download aria-hidden />
        Export CSV
      </Button>
      {canManage ? (
        <Button
          type="button"
          size="sm"
          className="min-h-11 sm:min-h-8"
          disabled={newPicked.length === 0}
          onClick={() => {
            setEmailIds(newPicked.map((row) => row.id));
          }}
        >
          <Mail aria-hidden />
          Email courier
        </Button>
      ) : null}
      {canManage ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11 sm:min-h-8"
          disabled={newPicked.length === 0 || markSent.isPending}
          onClick={() => {
            markSent.mutate(newPicked.map((row) => row.id));
          }}
        >
          {markSent.isPending ? (
            <Loader2 className="animate-spin" aria-hidden />
          ) : (
            <Check aria-hidden />
          )}
          Mark as sent
        </Button>
      ) : null}
    </>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <EscalationMailBar canManage={canManage} />
      <Tabs
        active={tab}
        counts={counts.data}
        onChange={(next) => {
          setTab(next);
          setHistory([undefined]);
          setNote(null);
        }}
      />
      <DataTable
        fields={fields}
        rows={selection.viewing ? [...selection.rows.values()] : rows}
        getRowId={(row) => row.id}
        project={projectEscalationRow}
        sort={sort}
        onToggleSort={onToggleSort}
        onRowClick={(row) => {
          open(row.id);
        }}
        renderOverride={renderOverride}
        selection={{
          isSelected: (row) => selection.has(row.id),
          onToggle: (row, on) => {
            setNote(null);
            selection.set(row, on);
          },
          onTogglePage: (list, on) => {
            setNote(null);
            selection.setMany(list, on);
          },
          labelOf: (row) => `Select ${row.orderNumber} ${row.awb ?? ""}`.trim(),
          active: selection.size > 0,
        }}
        mobileRow={(row) => <EscalationMobileCard row={row} />}
        emptyState={
          filter.kind === "group" && filter.children.length === 0 && !search ? (
            <p className="text-muted-foreground p-8 text-center text-sm">
              Nothing in {ESCALATION_STATUS_LABELS[tab]}.
            </p>
          ) : (
            <NoMatches onClear={onClearFilter} />
          )
        }
        footer={
          selection.viewing ? undefined : (
            <Pagination
              page={query.data}
              limit={limit}
              onLimitChange={(next) => {
                setLimit(next);
                setHistory([undefined]);
              }}
              history={history}
              onNavigate={setHistory}
            />
          )
        }
      />

      <SelectionBar
        selection={selection}
        onPage={facts.onPage}
        notInView={facts.notInView}
        pageFullySelected={facts.pageFullySelected}
        matching={query.data.total}
        onSelectAllMatching={selectAllMatching}
        noun={NOUN}
        actions={actions}
        note={note}
      />

      {emailIds !== null ? (
        <EmailCourierDialog
          ids={emailIds}
          onClose={() => {
            setEmailIds(null);
          }}
          onDone={(message) => {
            setEmailIds(null);
            setNote(message);
            selection.clear();
          }}
        />
      ) : null}

      {recordId !== null ? (
        <RecordDrawer
          title={
            rows.find((row) => row.id === recordId)?.orderNumber ?? "Escalation"
          }
          description="Delivery escalation"
        >
          <div className="min-h-0 flex-1 overflow-auto">
            <EscalationDetail escalationId={recordId} />
          </div>
        </RecordDrawer>
      ) : null}
    </div>
  );
}
